// src/pages/KnowledgeBasePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { knowledgeBaseAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, Textarea, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { BookOpen, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { format } from 'date-fns';
import styles from './KnowledgeBasePage.module.css';

const CATEGORIES = ['General', 'Production', 'Inventory', 'Recipes', 'Procedures', 'Suppliers'];

export default function KnowledgeBasePage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await knowledgeBaseAPI.list(effectiveCompanyId || (isGaafAdmin ? undefined : companyId));
            setEntries(res.data.entries?.filter(e => e.status !== 'deleted') || []);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, companyId, isGaafAdmin]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await knowledgeBaseAPI.delete(deleteTarget.entryId);
            toast.success('Entry Deleted');
            setDeleteTarget(null);
            load();
        } catch (_) { toast.error('Failed to Delete Entry'); }
        setDeleteLoading(false);
    };

    // Filter entries by search, then group by category
    const searchedEntries = entries.filter(e =>
        !search ||
        e.title?.toLowerCase().includes(search.toLowerCase()) ||
        e.content?.toLowerCase().includes(search.toLowerCase()) ||
        e.category?.toLowerCase().includes(search.toLowerCase()) ||
        (e.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
    );

    const grouped = CATEGORIES.reduce((acc, cat) => {
        const items = searchedEntries.filter(e => e.category === cat);
        if (items.length > 0) acc[cat] = items;
        return acc;
    }, {});
    const uncategorized = searchedEntries.filter(e => !CATEGORIES.includes(e.category));
    if (uncategorized.length > 0) grouped['other'] = uncategorized;

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Knowledge Base"
                description="Feed the AI Assistant with Company-Specific Knowledge — Production Recipes, Procedures, Supplier Info"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add Entry</Button>
                    </div>
                }
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by Title, Content, Category, Tags…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading ? (
                <div className={styles.list}>{[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : searchedEntries.length === 0 ? (
                <EmptyState
                    icon={<BookOpen size={40} />}
                    title={search ? 'No Entries match your search' : 'Knowledge Base is Empty'}
                    description={search ? 'Try a different Title, Tag, or Category.' : 'Add Entries so the AI can Answer Questions specific to your Company — Production Recipes, Ingredient Requirements, Standard Procedures.'}
                    action={!search && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add First Entry</Button>}
                />
            ) : (
                Object.entries(grouped).map(([category, items]) => (
                    <div key={category} className={styles.group}>
                        <div className={styles.groupLabel}>{category.charAt(0).toUpperCase() + category.slice(1)}</div>
                        <div className={styles.list}>
                            {items.map(entry => (
                                <KBCard key={entry.entryId} entry={entry} onEdit={() => setEditing(entry)} onDelete={() => setDeleteTarget(entry)} />
                            ))}
                        </div>
                    </div>
                ))
            )}

            <ConfirmModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleteLoading}
                title="Delete Entry" message={`Delete "${deleteTarget?.title}"? The AI will no longer use this Knowledge.`} confirmLabel="Delete Entry" />
            <KBModal open={showCreate || !!editing} entry={editing} companyId={effectiveCompanyId}
                onClose={() => { setShowCreate(false); setEditing(null); }}
                onSaved={() => { setShowCreate(false); setEditing(null); load(); }} />
        </div>
    );
}

function KBCard({ entry, onEdit, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <Card className={styles.kbCard} hover>
            <div className={styles.kbHeader}>
                <div className={styles.kbTitle} onClick={() => setExpanded(p => !p)}>{entry.title}</div>
                <div className={styles.kbActions}>
                    <button className={styles.iconBtn} onClick={onEdit} title="Edit"><Pencil size={14} /></button>
                    <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={onDelete} title="Delete"><Trash2 size={14} /></button>
                </div>
            </div>
            {entry.tags?.length > 0 && (
                <div className={styles.kbTags}>
                    {entry.tags.map(tag => <span key={tag} className={styles.tag}>#{tag}</span>)}
                </div>
            )}
            {expanded && <div className={styles.kbContent}>{entry.content}</div>}
            <div className={styles.kbFooter}>
                <span className={styles.kbDate}>{entry.createdAt ? format(new Date(entry.createdAt), 'MM/dd/yyyy') : '—'}</span>
                <button className={styles.expandBtn} onClick={() => setExpanded(p => !p)}>{expanded ? 'Show Less' : 'Show More'}</button>
            </div>
        </Card>
    );
}

function KBModal({ open, entry, companyId, onClose, onSaved }) {
    const [form, setForm] = useState({ title: '', content: '', category: 'General', tags: '' });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (entry) { setForm({ title: entry.title || '', content: entry.content || '', category: entry.category || 'General', tags: (entry.tags || []).join(', ') }); }
        else { setForm({ title: '', content: '', category: 'General', tags: '' }); }
    }, [entry, open]);

    const handleSave = async () => {
        if (!form.title || !form.content) return toast.error('Title and Content are Required');
        setLoading(true);
        try {
            const payload = { companyId, title: form.title, content: form.content, category: form.category, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
            if (entry) { await knowledgeBaseAPI.update(entry.entryId, payload); toast.success('Entry Updated'); }
            else { await knowledgeBaseAPI.create(payload); toast.success('Entry Created'); }
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Save Entry'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={entry ? 'Edit Entry' : 'Add Knowledge Base Entry'} size="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>This Information will be included in the AI's context when Answering Inventory Questions.</p>
                <Input label="Title *" value={form.title} onChange={e => set('title', e.target.value)} placeholder="GAAF Robusto Production Recipe" />
                <Select label="Category" value={form.category} onChange={e => set('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </Select>
                <Textarea label="Content *" value={form.content} onChange={e => set('content', e.target.value)} placeholder="Para producir 1 caja de GAAF Robusto se necesita 0.5 quintal de hoja Jalapa..." style={{ minHeight: 140 }} />
                <Input label="Tags (Comma separated)" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="Cohiba, Robusto, Recipe" />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>{entry ? 'Save Changes' : 'Add Entry'}</Button>
                </div>
            </div>
        </Modal>
    );
}
